const DEFAULT_ERROR_MESSAGE = '操作暂时没有完成，请稍后重试。';

const CODE_MESSAGES = {
  ERR_NETWORK: '网络连接失败，请检查网络后重试。',
  ECONNABORTED: '请求等待超时，请先查看处理结果。',
  ETIMEDOUT: '请求等待超时，请先查看处理结果。',
  ERR_CANCELED: '请求已取消。',
  UNAUTHORIZED: '登录状态已失效，请重新登录。',
  UNAUTHENTICATED: '登录状态已失效，请重新登录。',
  TOKEN_EXPIRED: '登录状态已过期，请重新登录。',
  FORBIDDEN: '当前账号没有此操作权限，请联系管理员。',
  ACCESS_DENIED: '当前账号没有此操作权限，请联系管理员。',
  INSUFFICIENT_CREDITS: '可用积分不足，请补充积分后再试。',
  INSUFFICIENT: '可用积分不足，请补充积分后再试。',
  RATE_LIMITED: '操作过于频繁，请稍后重试。',
  TOO_MANY_REQUESTS: '操作过于频繁，请稍后重试。',
  VERIFICATION_CODE_INVALID: '验证码不正确，请检查后重试。',
  VERIFICATION_CODE_EXPIRED: '验证码已过期，请重新获取。',
  CONTENT_POLICY_VIOLATION: '内容未通过安全检查，请调整内容后重试。',
  CONTENT_FILTERED: '内容未通过安全检查，请调整内容后重试。',
  FILE_TOO_LARGE: '文件大小超过限制，请压缩后重新上传。',
};

// Only presentation uses this formatter. The original Error, HTTP envelope and
// machine codes stay intact for cancellation, retry and payment reconciliation.
export function safeChineseMessage(value) {
  if (typeof value !== 'string') return '';
  const message = value.trim();
  if (!message || !/[\u4e00-\u9fff]/.test(message)) return '';
  const prose = message
    .replace(/\bNano Banana(?:\s+(?:Pro|\d+(?:\.\d+)*))?\b|\bGPT[ -]+Image(?:\s+[\d.]+)?\b|\bQwen(?:\s+(?:Image|Audio|Edit))*\b/gi, '')
    .replace(/\b(?:AI|API|HTTP|HTTPS|JSON|JPG|JPEG|PNG|WEBP|GIF|SVG|MP3|MP4|WAV|MB|KB|GB|ID)\b/g, '');
  // An unknown English sentence remains technical copy even with a Chinese prefix.
  if (/\b[a-z][a-z'-]*[ \t]+[a-z][a-z'-]*\b/i.test(prose)) return '';
  if (/\b(?:failed|failure|error|invalid|unsupported|unavailable|unauthori[sz]ed|forbidden|denied|exception|missing|expired|exceeded|unexpected|insufficient)\b|\b(?:not found|not allowed|not supported|api key|connection refused)\b/i.test(message)) return '';
  if (message.length > 600 || /<(?:!doctype|html|body|script)\b|SQLSTATE|\b(?:TypeError|SyntaxError|ReferenceError|AxiosError|Exception|undefined|null)\b|\b(?:failed to fetch|network error|request failed|status code|cannot read properties|internal server error|unexpected token)\b|\bat\s+\S+\s*\([^)]*:\d+|https?:\/\/\S+|\b[A-Z][A-Z0-9]+_[A-Z0-9_]+\b/i.test(message)) return '';
  return message;
}

function messageCandidates(error) {
  if (typeof error === 'string') return [error];
  const body = error?.response?.data;
  return [
    body?.error?.message, body?.error, body?.message,
    error?.error?.message, error?.error, error?.message,
  ];
}

export function getErrorMessage(error, fallback = DEFAULT_ERROR_MESSAGE) {
  const candidates = messageCandidates(error);
  const translated = (() => {
    // Preserve useful business guidance, including amounts, file limits and IDs.
    for (const candidate of candidates) {
      const chinese = safeChineseMessage(candidate);
      if (chinese) return chinese;
    }

    const body = error?.response?.data;
    const code = String(body?.data?.error_code || body?.error_code || body?.error?.code || (typeof body?.code === 'string' ? body.code : '')
      || error?.errorCode || error?.code || error?.name || '').toUpperCase();
    if (CODE_MESSAGES[code]) return CODE_MESSAGES[code];
    if (code === 'ABORTERROR') return '请求已取消。';
    const status = Number(error?.response?.status || error?.status || body?.code);
    if (status === 401) return CODE_MESSAGES.UNAUTHORIZED;
    if (status === 403) return CODE_MESSAGES.FORBIDDEN;
    const raw = candidates.filter(value => typeof value === 'string').join(' ');
    if (/network error|failed to fetch|network request failed|load failed|ERR_NETWORK|ECONNRESET|ERR_CONNECTION|network connection/i.test(raw)) return CODE_MESSAGES.ERR_NETWORK;
    if (/timeout|timed? ?out|ETIMEDOUT/i.test(raw)) return CODE_MESSAGES.ETIMEDOUT;
    if (/too many (?:requests|attempts)|rate limit/i.test(raw)) return CODE_MESSAGES.RATE_LIMITED;
    if (/insufficient (?:credits|points|balance)/i.test(raw)) return '服务可用额度不足，暂时无法完成操作，请稍后重试或联系客服。';
    if (/content[_ -]?(?:policy|filter)|safety (?:check|policy)|sensitive content/i.test(raw)) return CODE_MESSAGES.CONTENT_FILTERED;
    if (/payload too large|file too large|request entity too large/i.test(raw)) return CODE_MESSAGES.FILE_TOO_LARGE;
    if (/unauthenticated|unauthorized|token (?:has )?expired|jwt expired/i.test(raw)) return '服务身份验证失败，暂时无法完成操作，请稍后重试或联系客服。';
    if (/forbidden|permission denied|access denied/i.test(raw)) return CODE_MESSAGES.FORBIDDEN;

    if (status === 413) return CODE_MESSAGES.FILE_TOO_LARGE;
    if (status === 429) return CODE_MESSAGES.RATE_LIMITED;
    if ([408, 504, 524].includes(status)) return CODE_MESSAGES.ETIMEDOUT;
    return safeChineseMessage(fallback) || DEFAULT_ERROR_MESSAGE;
  })();
  const reminders = [...new Set(candidates.filter(value => typeof value === 'string')
    .flatMap(value => value.match(/(?:请)?(?:勿|不要|避免)重复(?:提交|付款|支付|操作)/g) || []))];
  const missing = reminders.filter(reminder => !translated.includes(reminder));
  if (!missing.length) return translated;
  const message = translated.replace(/请稍后重试/g, '请稍后查看处理结果').replace(/后重试/g, '后查看处理结果');
  return `${message.replace(/[。；;]$/, '')}。${missing.join('；')}。`;
}
